import React, { useEffect, useMemo, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Switch,
    FormControlLabel,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Avatar,
    Typography,
    InputAdornment,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddLinkIcon from '@mui/icons-material/AddLink'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'

const toSlug = (s = '') =>
    s
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '')

const isProbablyImageUrl = (url = '') =>
    /^https?:\/\/.+/i.test(url) && /\.(png|jpg|jpeg|webp|gif|svg)(\?.*)?$/i.test(url)

const splitTradeNames = (value = '') =>
    value
        .split(/[\s,;]+/g) // пробелы/запятые/; / переносы строки
        .map((x) => x.trim())
        .filter(Boolean)

const uniqUpper = (arr = []) => {
    const seen = new Set()
    const out = []
    for (const v of arr) {
        const u = String(v).trim().toUpperCase()
        if (!u) continue
        if (!seen.has(u)) {
            seen.add(u)
            out.push(u)
        }
    }
    return out
}

/** Пароль из 4 цифр для разблокировки полей логин/пароль сайтов */
const CREDENTIALS_UNLOCK_PIN = '2847'

export default function AddSupplierModal({
                                             open,
                                             onClose,
                                             onSubmit,
                                             loading = false,

                                             suppliers = [],
                                             onDelete,
                                             deletingTradeName = null,

                                             // ✅ NEW: edit props
                                             onUpdate, // (payload) => Promise
                                             updatingTradeName = null, // чтобы показать busy на строке (опционально)
                                         }) {
    const [mode, setMode] = useState('add') // 'add' | 'edit'
    const [editingMeta, setEditingMeta] = useState({ id: null, oldTradeName: null })

    const [form, setForm] = useState({
        name: '',
        link: '',
        tradeNames: [],
        tradeNameInput: '',
        iconUrl: '',
        permission: true,
        enabled: true,
        disabled: false,
        siteCredentials: [{ siteUrl: '', login: '', password: '' }],
    })
    const [credentialsUnlocked, setCredentialsUnlocked] = useState(false)
    const [unlockPasswordInput, setUnlockPasswordInput] = useState('')

    const resetForm = () => {
        setMode('add')
        setEditingMeta({ id: null, oldTradeName: null })
        setForm({
            name: '',
            link: '',
            tradeNames: [],
            tradeNameInput: '',
            iconUrl: '',
            permission: true,
            enabled: true,
            disabled: false,
            siteCredentials: [{ siteUrl: '', login: '', password: '' }],
        })
        setCredentialsUnlocked(false)
        setUnlockPasswordInput('')
    }

    useEffect(() => {
        if (!open) return
        resetForm()
    }, [open])

    const startEdit = (s) => {
        const tradeNames = uniqUpper(splitTradeNames(String(s.tradeName ?? '')))
        const siteCredentials = Array.isArray(s?.siteCredentials) && s.siteCredentials.length > 0
            ? s.siteCredentials.map((c) => ({
                siteUrl: c?.siteUrl ?? '',
                login: c?.login ?? '',
                password: c?.password ?? '',
            }))
            : [{ siteUrl: '', login: '', password: '' }]

        setMode('edit')
        setEditingMeta({ id: s?._id ?? null, oldTradeName: s?.tradeName ?? null })

        setForm({
            name: s?.name ?? '',
            link: s?.link ?? '',
            tradeNames,
            tradeNameInput: tradeNames.join(', '),
            iconUrl: s?.iconUrl ?? '',
            permission: typeof s?.permission === 'boolean' ? s.permission : true,
            enabled: typeof s?.enabled === 'boolean' ? s.enabled : true,
            disabled: typeof s?.disabled === 'boolean' ? s.disabled : false,
            siteCredentials,
        })
        setCredentialsUnlocked(false)
        setUnlockPasswordInput('')
    }

    const errors = useMemo(() => {
        const e = {}
        if (!form.name.trim()) e.name = 'Required'
        if (!form.link.trim()) e.link = 'Required'
        if (!form.tradeNames?.length) e.tradeName = 'Required'

        if (form.iconUrl.trim() && !/^https?:\/\/.+/i.test(form.iconUrl.trim())) {
            e.iconUrl = 'Must be a valid http(s) URL'
        }

        return e
    }, [form])

    const canSubmit = Object.keys(errors).length === 0 && !loading

    const sortedSuppliers = useMemo(() => {
        const arr = Array.isArray(suppliers) ? suppliers : []
        return [...arr].sort((a, b) => {
            const ae = a?.enabled ? 0 : 1
            const be = b?.enabled ? 0 : 1
            if (ae !== be) return ae - be
            return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
        })
    }, [suppliers])

    const handleSave = () => {
        const siteCredentials = (form.siteCredentials || [])
            .map((c) => ({
                siteUrl: (c.siteUrl || '').trim(),
                login: (c.login || '').trim(),
                password: (c.password || '').trim(),
            }))
            .filter((c) => c.siteUrl || c.login || c.password)

        const payload = {
            ...form,
            tradeName: (form.tradeNames || []).join(','),
            siteCredentials: siteCredentials.length ? siteCredentials : [{ siteUrl: '', login: '', password: '' }],
        }

        if (mode === 'edit' && typeof onUpdate === 'function') {
            onUpdate({
                ...payload,
                id: editingMeta.id != null ? String(editingMeta.id) : undefined,
                oldTradeName: editingMeta.oldTradeName || undefined,
            })
            return
        }

        onSubmit(payload)
    }

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                {mode === 'edit' ? 'Edit Supplier' : 'Add Supplier'}
                <Chip size="small" label="DB" variant="outlined" />
                {mode === 'edit' ? <Chip size="small" label="edit mode" color="warning" /> : null}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label="Saved to DB" variant="outlined" />
                        <Chip label="Icon: URL or preset" variant="outlined" />
                        <Chip label="Multi tradeName" variant="outlined" />
                    </Box>

                    <Divider />

                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(e) => {
                            const name = e.target.value
                            setForm((p) => ({
                                ...p,
                                name,
                                link: p.link ? p.link : toSlug(name),
                            }))
                        }}
                        placeholder="Makro"
                        error={!!errors.name}
                        helperText={errors.name || ' '}
                        fullWidth
                    />

                    <TextField
                        label="Link (route key)"
                        value={form.link}
                        onChange={(e) => setForm((p) => ({ ...p, link: toSlug(e.target.value) }))}
                        placeholder="makro"
                        error={!!errors.link}
                        helperText={errors.link || 'Used for /admin/{link}'}
                        fullWidth
                    />

                    {/* ✅ TradeName(s) multi */}
                    <TextField
                        label="TradeName(s)"
                        value={form.tradeNameInput}
                        onChange={(e) => {
                            const tradeNameInput = e.target.value
                            const tradeNames = uniqUpper(splitTradeNames(tradeNameInput))
                            setForm((p) => ({ ...p, tradeNameInput, tradeNames }))
                        }}
                        onBlur={() => {
                            setForm((p) => ({
                                ...p,
                                tradeNameInput: (p.tradeNames || []).join(', '),
                            }))
                        }}
                        placeholder="MCH, KKS, SSQ"
                        error={!!errors.tradeName}
                        helperText={errors.tradeName || 'Multiple values: comma, space or Enter'}
                        fullWidth
                        multiline
                        minRows={2}
                    />

                    {form.tradeNames?.length ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {form.tradeNames.map((t) => (
                                <Chip
                                    key={t}
                                    label={t}
                                    size="small"
                                    onDelete={() => {
                                        setForm((p) => {
                                            const next = (p.tradeNames || []).filter((x) => x !== t)
                                            return {
                                                ...p,
                                                tradeNames: next,
                                                tradeNameInput: next.join(', '),
                                            }
                                        })
                                    }}
                                />
                            ))}
                        </Box>
                    ) : null}

                    {/* ✅ iconUrl */}
                    <TextField
                        label="Icon URL (optional)"
                        value={form.iconUrl}
                        onChange={(e) => setForm((p) => ({ ...p, iconUrl: e.target.value.trim() }))}
                        placeholder="https://.../icon.png"
                        error={!!errors.iconUrl}
                        helperText={
                            errors.iconUrl ||
                            'Paste a direct image URL (png/jpg/svg/webp). If empty — UI will use icon mapping by link.'
                        }
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AddLinkIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Avatar
                                        src={form.iconUrl && /^https?:\/\//i.test(form.iconUrl) ? form.iconUrl : undefined}
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            bgcolor: 'rgba(6,192,176,0.12)',
                                            border: '1px solid rgba(0,0,0,0.08)',
                                        }}
                                        variant="rounded"
                                    >
                                        🏬
                                    </Avatar>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {form.iconUrl && /^https?:\/\//i.test(form.iconUrl) && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px dashed rgba(0,0,0,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Avatar src={form.iconUrl} variant="rounded" sx={{ width: 44, height: 44 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Icon preview</Typography>
                                <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                                    {form.iconUrl}
                                </Typography>
                            </Box>
                            {!isProbablyImageUrl(form.iconUrl) && <Chip size="small" color="warning" label="Maybe not direct image" />}
                        </Box>
                    )}

                    <Divider />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Site credentials</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            type="password"
                            placeholder="PIN (4 digits)"
                            value={unlockPasswordInput}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 4)
                                setUnlockPasswordInput(v)
                                setCredentialsUnlocked(v === CREDENTIALS_UNLOCK_PIN)
                            }}
                            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {credentialsUnlocked ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 280 }}
                        />
                        <Chip
                            size="small"
                            icon={credentialsUnlocked ? <LockOpenIcon /> : <LockIcon />}
                            label={credentialsUnlocked ? 'Unlocked' : 'Locked'}
                            color={credentialsUnlocked ? 'success' : 'default'}
                            variant="outlined"
                        />
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            borderRadius: 2,
                            border: '1px solid rgba(0,0,0,0.08)',
                            p: 2,
                            bgcolor: 'rgba(0,0,0,0.02)',
                            filter: credentialsUnlocked ? 'none' : 'blur(5px)',
                            pointerEvents: credentialsUnlocked ? 'auto' : 'none',
                            userSelect: credentialsUnlocked ? 'text' : 'none',
                            transition: 'filter 0.2s ease',
                        }}
                    >
                        {(() => {
                            const cred = (form.siteCredentials || [{ siteUrl: '', login: '', password: '' }])[0]
                            const setCred = (field, value) =>
                                setForm((p) => ({
                                    ...p,
                                    siteCredentials: [{ ...(p.siteCredentials?.[0] || {}), [field]: value }],
                                }))
                            return (
                                <Box sx={{ display: 'grid', gap: 1.5 }}>
                                    <TextField
                                        size="small"
                                        label="Site URL"
                                        placeholder="https://..."
                                        value={cred.siteUrl}
                                        onChange={(e) => setCred('siteUrl', e.target.value)}
                                        fullWidth
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            label="Login"
                                            placeholder="login"
                                            value={cred.login}
                                            onChange={(e) => setCred('login', e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            size="small"
                                            type="password"
                                            label="Password"
                                            placeholder="••••••"
                                            value={cred.password}
                                            onChange={(e) => setCred('password', e.target.value)}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            )
                        })()}
                    </Box>

                    <Box sx={{ display: 'grid', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!form.permission}
                                    onChange={(e) => setForm((p) => ({ ...p, permission: e.target.checked }))}
                                />
                            }
                            label="Permission (allow access)"
                        />
                        <FormControlLabel
                            control={
                                <Switch checked={!!form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} />
                            }
                            label="Enabled (show in menu)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!form.disabled}
                                    onChange={(e) => setForm((p) => ({ ...p, disabled: e.target.checked }))}
                                />
                            }
                            label="Disabled (coming soon)"
                        />
                    </Box>

                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(6,192,176,0.08)' }}>
                        <Box sx={{ fontSize: 13, opacity: 0.8 }}>Preview route:</Box>
                        <Box sx={{ fontWeight: 800 }}>/admin/{form.link || '...'}</Box>
                    </Box>

                    {/* ✅ список существующих suppliers + edit/delete */}
                    <Divider />

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontWeight: 800 }}>Existing suppliers</Typography>
                        <Chip size="small" label={sortedSuppliers.length} />
                    </Box>

                    <Box
                        sx={{
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <List dense sx={{ p: 0, maxHeight: 260, overflow: 'auto' }}>
                            {sortedSuppliers.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="No suppliers yet" />
                                </ListItem>
                            ) : (
                                sortedSuppliers.map((s) => {
                                    const deletingBusy = deletingTradeName && deletingTradeName === s.tradeName
                                    const updatingBusy = updatingTradeName && updatingTradeName === s.tradeName

                                    const canDelete = typeof onDelete === 'function' && !!s.tradeName
                                    const canEdit = typeof onUpdate === 'function'

                                    return (
                                        <ListItem
                                            key={s.tradeName || s.link || s.name}
                                            secondaryAction={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {canEdit ? (
                                                        <Tooltip title="Edit supplier">
                              <span>
                                <IconButton
                                    edge="end"
                                    onClick={() => startEdit(s)}
                                    disabled={!!updatingBusy || !!deletingBusy}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                                                        </Tooltip>
                                                    ) : null}

                                                    {canDelete ? (
                                                        <Tooltip title="Delete supplier">
                              <span>
                                <IconButton
                                    edge="end"
                                    onClick={() => onDelete(s.tradeName)}
                                    disabled={!!deletingBusy || !!updatingBusy}
                                    color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                                                        </Tooltip>
                                                    ) : null}
                                                </Box>
                                            }
                                        >
                                            <Avatar
                                                src={s.iconUrl || undefined}
                                                variant="rounded"
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    mr: 1.5,
                                                    bgcolor: 'rgba(0,0,0,0.04)',
                                                    border: '1px solid rgba(0,0,0,0.06)',
                                                }}
                                            >
                                                🏬
                                            </Avatar>

                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{s.name}</Typography>
                                                        {s.enabled ? <Chip size="small" label="enabled" /> : <Chip size="small" label="hidden" variant="outlined" />}
                                                        {s.disabled ? <Chip size="small" color="warning" label="disabled" /> : null}
                                                        {mode === 'edit' && (editingMeta.oldTradeName === s.tradeName || editingMeta.id === s._id) ? (
                                                            <Chip size="small" color="warning" variant="outlined" label="editing" />
                                                        ) : null}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                                                        link: {s.link} • tradeName: {s.tradeName}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    )
                                })
                            )}
                        </List>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={() => {
                        if (mode === 'edit') {
                            resetForm()
                            return
                        }
                        onClose?.()
                    }}
                    disabled={loading}
                    variant="outlined"
                >
                    {mode === 'edit' ? 'Back' : 'Cancel'}
                </Button>

                <Button onClick={handleSave} disabled={!canSubmit} variant="contained">
                    {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
